import { Fragment, useEffect, useState, useCallback, useRef } from "react";
import { persianLanguage } from "../../../../constants/persianTerms";
import MobileNumberForm from "./mobile-number-form/MobileNumberForm";
import GetAuthChallenge from "../../../api-layer/auth-challenge-api/GetAuthChallenge";
import { sha256 } from "../../../../shared/crypto-functions";
import LoginWithPassword from "./login-with-password/LoginWithPassword";
import { LOGIN_PAGE_REFRESH_INTERVAL } from "../../../../constants/constant-numbers";

export default function LoginPage() {
    const withOTP = {
        type: "otp",
        value: 1,
    };

    const withPass = {
        type: "password",
        value: 2,
    };

    const retryTimeoutRef = useRef(null);
    const MAX_ATTEMPTS = 100_000;

    const [authChallengeResult, setAuthChallengeResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [loginMethod, setLoginMethod] = useState("password"); // "password" | "otp"

    useEffect(() => {
        const timer = setTimeout(() => {
            window.location.reload();
        }, LOGIN_PAGE_REFRESH_INTERVAL);

        return () => clearTimeout(timer);
    }, []);

    const findChallengeAnswer = useCallback(
        async (authChallenge) => {
            if (
                !authChallenge?.salt ||
                !authChallenge?.hashed
            ) {
                setError(
                    "Invalid auth challenge data"
                );

                return;
            }

            setIsLoading(true);
            setError(null);
            setProgress(0);

            try {

                let found = false;

                // Backend generates:
                // 1 <= number <= 10,000,000
                for (
                    let number = 1;
                    number <= MAX_ATTEMPTS;
                    number++
                ) {
                    /*
                     * Update progress every 1000 attempts.
                     *
                     * We don't update React state on every
                     * iteration because that would cause
                     * unnecessary re-renders.
                     */
                    if (
                        number % 1000 === 0
                    ) {
                        const currentProgress =
                            (number /
                                MAX_ATTEMPTS) *
                            100;

                        setProgress(
                            currentProgress
                        );

                        /*
                         * Give the browser a chance
                         * to update the UI.
                         */
                        await new Promise(
                            (resolve) =>
                                setTimeout(
                                    resolve,
                                    0
                                )
                        );
                    }

                    const hashed =
                        await sha256(
                            `${authChallenge.salt}${number}`
                        );

                    if (
                        hashed ===
                        authChallenge.hashed
                    ) {
                        setProgress(100);

                        setAuthChallengeResult({
                            number: number,
                            salt: authChallenge.salt,
                            hashed: authChallenge.hashed,
                            id: authChallenge.id,
                        });

                        found = true;
                        break;
                    }
                }

                if (!found) {
                    setError(persianLanguage.phrase.general.RETRY_AGAIN);
                }
            } catch (error) {
                window.location.reload();
                setError(persianLanguage.phrase.general.RETRY_AGAIN);
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    const getAuthChallenge = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setProgress(0);

        try {
            const response = await GetAuthChallenge();
            await findChallengeAnswer(response.data);
        } catch (error) {
            setError(
                "Failed to get authentication challenge. Retrying..."
            );

            retryTimeoutRef.current = setTimeout(() => {
                getAuthChallenge();
            }, 10_000);
        }
    }, [findChallengeAnswer]);

    useEffect(() => {
        getAuthChallenge();
    }, [getAuthChallenge]);

    /*
     * Loading screen
     */
    if (
        isLoading &&
        !authChallengeResult
    ) {
        return (
            <div
                className="flex items-center justify-center min-h-screen"
                dir="rtl"
            >
                <div className="w-full max-w-md px-6">
                    <div className="text-center mb-4">
                        {persianLanguage.phrase.general.IS_LOADING}
                        ...
                    </div>

                    {/* Progress bar */}
                    <div
                        className="
                            w-full
                            h-3
                            bg-gray-200
                            rounded-full
                            overflow-hidden
                        "
                        dir="ltr"
                    >
                        <div
                            className="
                                h-full
                                bg-blue-500
                                transition-all
                                duration-150
                            "
                            style={{
                                width: `${progress}%`,
                            }}
                        />
                    </div>

                    {/* Percentage */}
                    <div className="text-center mt-2 text-sm text-gray-500">
                        {Math.round(progress)}%
                    </div>
                </div>
            </div>
        );
    }

    /*
     * Error screen
     */
    if (error) {
        return (
            <div
                className="flex items-center justify-center min-h-screen px-4"
                dir="rtl"
            >
                <div className="w-full max-w-md">
                    <div
                        className="
                            bg-white
                            border
                            border-red-200
                            rounded-xl
                            shadow-lg
                            p-6
                        "
                    >
                        {/* Error Icon */}
                        <div className="flex justify-center mb-4">
                            <div
                                className="
                                    flex
                                    items-center
                                    justify-center
                                    w-14
                                    h-14
                                    rounded-full
                                    bg-red-100
                                "
                            >
                                <svg
                                    className="w-7 h-7 text-red-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* Title */}
                        <h2 className="text-center text-lg font-semibold text-gray-800 mb-2">
                            {persianLanguage.phrase.error.ERROR_400}
                        </h2>

                        {/* Error Message */}
                        <p className="text-center text-sm text-gray-600 leading-6">
                            {/* {error} */}
                        </p>
    
                    </div>
                </div>
            </div>
        );
    }

    /*
     * Login screen
     */
    return (
        <Fragment>
            <div className="flex items-center justify-center min-h-screen p-4" dir="rtl">
                <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6">
                    {/* Login Method Toggle */}
                    <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
                        <button
                            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                                loginMethod === "password"
                                    ? "bg-white shadow-sm text-blue-600"
                                    : "text-gray-600 hover:text-gray-900"
                            }`}
                            onClick={() => setLoginMethod("password")}
                        >
                            {persianLanguage.word.user.LOGIN} {persianLanguage.word.general.WITH} {persianLanguage.word.user.PASSWORD}
                        </button>
                        <button
                            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                                loginMethod === "otp"
                                    ? "bg-white shadow-sm text-blue-600"
                                    : "text-gray-600 hover:text-gray-900"
                            }`}
                            onClick={() => setLoginMethod("otp")}
                        >
                            {persianLanguage.word.user.LOGIN} {persianLanguage.word.general.WITH} {persianLanguage.word.auth.OTP}
                        </button>
                    </div>

                    {/* Login Form */}
                    {loginMethod === "password" ? (
                        <LoginWithPassword
                            loginType={withPass}
                            authChallengeResult={authChallengeResult}
                            onChallengeMismatch={getAuthChallenge}
                        />
                    ) : (
                        <MobileNumberForm
                            loginType={withOTP}
                            authChallengeResult={authChallengeResult}
                        />
                    )}
                </div>
            </div>
        </Fragment>
    );
}